import {
  ConfirmationAlert,
  ConfirmationCard,
  ConfirmationChips,
  ConfirmationField,
  ConfirmationFields,
  ConfirmationPanel,
  ConfirmationTags,
} from "@/shared/foundation/primitives";

import type { ConfirmationContentProps } from "./ClientTools";
import { RESOURCE_TYPE_LABELS } from "./collectionResourceIds";
import {
  coerceCreateCollectionInput,
  type CreateCollectionParams,
} from "./collectionToolInput";

/**
 * Read-only preview of a client_create_collection proposal.
 *
 * Rendered inline on the approval card, not only behind "More details": this
 * is the tool the design pins to REQUIRE_APPROVAL, so the facts a user needs
 * to judge it — name, resource type, members — have to be on the default,
 * lowest-friction path rather than behind an optional disclosure.
 *
 * `resource_type` is surfaced as its own prominent chip rather than folded
 * into the field list: it's fixed for the life of the collection, so it's
 * the detail most worth double-checking before approving. Initial members
 * are shown as bare IDs with a count — no per-member fetch, since the LLM
 * may propose dozens at once and the raw ID is enough to sanity-check.
 *
 * A proposal `execute` would refuse renders as an alert and nothing else:
 * presenting a coherent card for a write that cannot succeed invites an
 * approval that achieves nothing.
 */
export function CreateCollectionConfirmation({
  params,
}: ConfirmationContentProps<CreateCollectionParams>) {
  // The same verdict `execute` will reach, so what is approvable is what is
  // sendable.
  const verdict = coerceCreateCollectionInput(params);

  if (!verdict.ok) {
    return (
      <ConfirmationPanel label="Create collection preview">
        <ConfirmationAlert>
          This request won&rsquo;t succeed. {verdict.reason}
        </ConfirmationAlert>
      </ConfirmationPanel>
    );
  }

  const { name, resourceType, description, tags, resources } = verdict.payload;

  return (
    <ConfirmationPanel label="Create collection preview">
      <ConfirmationCard
        title={name}
        badge={RESOURCE_TYPE_LABELS[resourceType][1]}
      >
        <ConfirmationFields>
          {description && (
            <ConfirmationField label="Description">
              {description}
            </ConfirmationField>
          )}

          {tags.length > 0 && (
            <ConfirmationField label="Tags">
              <ConfirmationTags tags={tags} />
            </ConfirmationField>
          )}

          {resources.length > 0 && (
            <ConfirmationField label={`Initial members (${resources.length})`}>
              <ConfirmationChips items={resources} />
            </ConfirmationField>
          )}
        </ConfirmationFields>
      </ConfirmationCard>
    </ConfirmationPanel>
  );
}
